class VideoReelSection extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.autoplay = this.dataset.autoplay === 'true';
    this.videos = Array.from(this.querySelectorAll('video.vrs-video'));
    this.cleanups = [];
    this.handleVisibilityChange = this.onVisibilityChange.bind(this);

    this.prepareVideos();

    if (this.autoplay) {
      this.enableAutoplay();
    } else {
      this.enableManualPlayback();
    }
  }

  disconnectedCallback() {
    this.cleanups.forEach((cleanup) => cleanup());
    this.cleanups = [];

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.initialized = false;
  }

  prepareVideos() {
    this.videos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
    });
  }

  enableAutoplay() {
    if (!this.videos.length) return;

    if (!('IntersectionObserver' in window)) {
      this.videos.forEach((video) => this.playVideo(video));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (entry.isIntersecting) {
            this.playVideo(video);
          } else {
            video.pause();
          }
        });
      },
      {
        root: null,
        rootMargin: '160px 0px',
        threshold: 0.15,
      }
    );

    this.videos.forEach((video) => this.observer.observe(video));
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.cleanups.push(() => document.removeEventListener('visibilitychange', this.handleVisibilityChange));
  }

  enableManualPlayback() {
    this.videos.forEach((video) => {
      const card = video.closest('.vrs-card');
      const videoWrap = video.closest('[data-video-wrap]');
      const icon = card?.querySelector('.vrs-play-icon');

      if (!card || !videoWrap) return;

      const showIcon = () => {
        if (icon) icon.style.opacity = '1';
      };
      const hideIcon = () => {
        if (icon) icon.style.opacity = '0';
      };
      const play = () => {
        this.playVideo(video);
        hideIcon();
      };
      const pause = () => {
        video.pause();
        video.currentTime = 0;
        showIcon();
      };
      const toggleFromVideoArea = (event) => {
        const productLink = event.target.closest('[data-product-link]');

        if (productLink && !video.paused) return;

        event.preventDefault();
        if (video.paused) {
          play();
        } else {
          pause();
        }
      };

      card.addEventListener('mouseenter', play);
      card.addEventListener('mouseleave', pause);
      videoWrap.addEventListener('click', toggleFromVideoArea);

      this.cleanups.push(() => {
        card.removeEventListener('mouseenter', play);
        card.removeEventListener('mouseleave', pause);
        videoWrap.removeEventListener('click', toggleFromVideoArea);
      });
    });
  }

  playVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;

    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.videos.forEach((video) => video.pause());
      return;
    }

    if (this.autoplay && this.observer) {
      this.videos.forEach((video) => this.observer.unobserve(video));
      this.videos.forEach((video) => this.observer.observe(video));
    }
  }
}

if (!customElements.get('video-reel-section')) {
  customElements.define('video-reel-section', VideoReelSection);
}
